import { useEffect, useState } from 'react';
import { endOfMonth, endOfYear, startOfMonth, startOfYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import CalendarPeriodSelector from '@/features/calendar/components/CalendarPeriodSelector';
import CalendarPeriodSwitcher from '@/features/calendar/components/CalendarPeriodSwitcher';
import CalendarPageFilter from '@/features/calendar/components/CalendarPageFilter';
import CalendarDaily from '@/features/calendar/components/CalendarDaily/CalendarDaily';
import CalendarMonthly from '@/features/calendar/components/CalendarMonthly';
import { useCalendarApi } from '@/features/calendar/hooks/useCalendarApi';
import CalendarApartmentSelector from '@/features/calendar/components/CalendarApartmentSelector';
import ReservationDetails from '@/features/reservations/components/ReservationDetails';
import { Button } from '@/components/ui/button';
import CalendarAddIcon from '@/components/icons/CalendarAddIcon';
import ReservationManage from '@/features/reservations/components/ReservationManage';
import { DocumentType } from '@/features/calendar/types/calendar-dto';
import FilterIcon from '@/components/icons/FilterIcon';
import { useCalendarFilter } from '@/features/calendar/hooks/useCalendarFilter';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import { FilterHelper } from '@/utils/filterHelper';
import CalendarMonthlySingle from '@/features/calendar/components/CalendarMonthlySingle';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

const CalendarPage = () => {
	const [apartmentId, setApartmentId] = useState('');
	const [reservationApartmentId, setReservationApartmentId] = useState('');
	const [reservationId, setReservationId] = useState('');
	const [reservationType, setReservationType] = useState<DocumentType>();
	const [reservationCheckIn, setReservationCheckIn] = useState<Date>();
	const [reservationCheckOut, setReservationCheckOut] = useState<Date>();
	const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
	const [selectedDate, setSelectedDate] = useState(new Date());
	const [searchText, setSearchText] = useState('');
	const [showReservationDetails, setShowReservationDetails] = useState(false);
	const [showReservationManage, setShowReservationManage] = useState(false);
	const [showCalendarFilter, setShowCalendarFilter] = useState(false);
	const [apartmendDetailsId, setApartmentDetailsId] = useState('');

	const { t } = useTranslation();
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { filter } = useCalendarFilter();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

	useEffect(() => {
		setViewMode(isMobile ? 'year' : 'month');
	}, [isMobile]);

	const { calendarEventsListData, isFetchingCalendarEventsListData } =
		useCalendarApi({
			startDate:
				filter.startDate ??
				(viewMode === 'month'
					? startOfMonth(selectedDate)
					: startOfYear(selectedDate)),
			endDate:
				filter.endDate ??
				(viewMode === 'month'
					? endOfMonth(selectedDate)
					: endOfYear(selectedDate)),
			searchText,
			apartmentId,
			types: filter.types,
		});

	const handleShowReservationDetails = (
		id: string,
		type: DocumentType,
		apartmentId?: string,
		checkInDate?: Date,
		checkOutDate?: Date,
	) => {
		setReservationId(id);
		setApartmentDetailsId(apartmentId || '');
		setReservationType(type);
		setReservationCheckIn(checkInDate);
		setReservationCheckOut(checkOutDate);
		setShowReservationDetails(true);
	};

	const handleSwitchPeriod = (period: 'month' | 'year') => {
		if (period === 'month') setApartmentId('');
		setViewMode(period);
	};

	const filtersQuantity = FilterHelper.countFilterValues(filter);

	const handleShowEditReservation = (id: string) => {
		setShowReservationDetails(false);
		setReservationApartmentId(id);
		setShowReservationManage(true);
	};

	const handleNewReservation = () => {
		setReservationId('');
		setReservationApartmentId('');
		setShowReservationManage(true);
	};

	return (
		<section
			className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
			<EPageHeader
				title={t('main.calendar.title')}
				search={
					<div
						className={cn(
							'w-full flex items-center gap-4',
							isMobile && 'gap-2',
						)}>
						{viewMode === 'year' && (
							<div className={cn(isMobile && 'flex-1')}>
								<CalendarApartmentSelector
									selectedDate={selectedDate}
									setApartmentId={setApartmentId}
									containerClassName={cn(
										isMobile && 'w-full',
									)}
								/>
							</div>
						)}
						<CalendarPeriodSelector
							viewMode={viewMode}
							selectedDate={filter.startDate ?? selectedDate}
							setSelectedDate={setSelectedDate}
							containerClassName={cn(isMobile && 'flex-1')}
							isMobile={isMobile}
						/>
					</div>
				}>
				{!isMobile && (
					<CalendarPeriodSwitcher
						viewMode={viewMode}
						setViewMode={handleSwitchPeriod}
					/>
				)}
				<div className='relative'>
					{!!filtersQuantity && (
						<div className='absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse '>
							{filtersQuantity}
						</div>
					)}
					<Button
						variant='outline'
						size={isMobile ? 'icon' : 'default'}
						onClick={() => setShowCalendarFilter(true)}>
						<FilterIcon />
						{!isMobile && t('elements.buttons.filter')}
					</Button>
				</div>
				{permissions?.createBookings && (
					<Button
						size={isMobile ? 'icon' : 'default'}
						onClick={handleNewReservation}>
						<CalendarAddIcon className='stroke-popover-10' />
						{!isMobile && t('elements.buttons.newReservation')}
					</Button>
				)}
			</EPageHeader>
			<div className='h-full flex flex-col gap-6 overflow-hidden'>
				{viewMode === 'month' && !isMobile && (
					<CalendarDaily
						apartments={calendarEventsListData?.apartments}
						selectedDate={selectedDate}
						searchText={searchText}
						setSearchText={setSearchText}
						showReservationDetails={handleShowReservationDetails}
					/>
				)}
				{viewMode === 'year' && !isMobile && (
					<CalendarMonthly
						apartments={calendarEventsListData?.apartments}
						selectedDate={selectedDate}
						isLoading={isFetchingCalendarEventsListData}
						showReservationDetails={handleShowReservationDetails}
					/>
				)}
				{isMobile && (
					<CalendarMonthlySingle
						apartments={calendarEventsListData?.apartments}
						selectedDate={selectedDate}
						setSelectedDate={setSelectedDate}
						isLoading={isFetchingCalendarEventsListData}
						showReservationDetails={handleShowReservationDetails}
						minWidth='280px'
					/>
				)}
				{showReservationDetails && (
					<ReservationDetails
						id={reservationId}
						documentType={reservationType}
						apartmentId={apartmendDetailsId}
						startDate={reservationCheckIn}
						endDate={reservationCheckOut}
						onEdit={handleShowEditReservation}
						onClose={() => setShowReservationDetails(false)}
						isMobile={isMobile}
					/>
				)}
				{showReservationManage && (
					<ReservationManage
						apartmentId={reservationApartmentId}
						reservationId={reservationId}
						onClose={() => setShowReservationManage(false)}
					/>
				)}
				{showCalendarFilter && (
					<CalendarPageFilter
						onClose={() => setShowCalendarFilter(false)}
						isMobile={isMobile}
					/>
				)}
			</div>
		</section>
	);
};

export default CalendarPage;
